import { votingIds } from "@artists/challenges-testkit"
import { assertIsDefined } from "@artists/devkit"
import type { FC } from "react"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "../../../../api"
import type { Challenge, EntryWithVote } from "../../../../api/hooks/vote"
import {
	AnalyticsPageTitle,
	createProductClickAnalytics,
	isVoting,
	useAnalytics,
} from "../../../../utils"
import { List } from "../../../@shared"
import { Skeletons } from "../../../@shared/vote-card"
import { UnregisteredUserPrompt, useUnregisteredUserPrompt } from "../warnings"
import { VoteCard } from "./card"

interface Props {
	loading?: boolean
	items: EntryWithVote[]
	challenge: Maybe<Challenge>
}

export const VotingList: FC<Props> = ({ loading, items, challenge }) => {
	const { t } = useTranslation("common")
	const { isOpen, onClose, onUserAction } = useUnregisteredUserPrompt()
	const { send } = useAnalytics()
	const { user } = useAuth()

	const sendAnalytics = useCallback(
		(entry: EntryWithVote, index: number) => () => {
			assertIsDefined(challenge)

			const analytics = createProductClickAnalytics({
				userId: user?.userId ?? null,
				designId: entry.designId,
				pageTitle: AnalyticsPageTitle.Vote,
				index,
				link: entry.pdpUrl,
				authorName: entry.user.userName,
				productName: challenge.product.name,
			})

			send(analytics)
		},
		[challenge, send, user?.userId],
	)

	return (
		<List
			loading={loading}
			empty={t("errors.noEntries")}
			loader={
				<Skeletons
					text={t("vote.vote")}
					votindActive={isVoting(challenge?.status.current.name)}
				/>
			}
			data-testid={votingIds.votingList}
			mb={{ sm: 12, xs: 9 }}
		>
			{items
				.filter(
					item =>
						item.entryState === "APPROVED" &&
						item.entryStatus === "PUBLIC_FOR_SALE",
				)
				.map((item, index) => (
					<VoteCard
						key={item.id}
						entry={item}
						onUserAction={onUserAction}
						onPdpClick={sendAnalytics(item, index)}
					/>
				))}
			<UnregisteredUserPrompt
				isOpen={isOpen}
				onClose={onClose}
				slug={challenge?.slug}
			/>
		</List>
	)
}
